import { ref, computed, watch } from '@vue/composition-api';

/**
 * Composable for tracking form dirty state and warning on unsaved changes
 * @param {Object} initialData - Initial form data (reactive)
 * @param {Object} currentData - Current form data (reactive)
 * @param {Object} options - Configuration options
 * @returns {Object} Dirty state utilities
 */
export function useFormDirty(initialData, currentData, options = {}) {
  const {
    deepCompare = true,
    warningMessage = 'Máte neuložené zmeny. Naozaj chcete odísť?',
    autoSave = false,
    autoSaveDelay = 3000,
  } = options;

  const isDirty = ref(false);
  const hasChanges = ref(false);
  const autoSaveTimer = ref(null);

  /**
   * Compare two values for equality
   */
  const isEqual = (a, b) => {
    if (deepCompare) {
      return JSON.stringify(a) === JSON.stringify(b);
    }
    return a === b;
  };

  /**
   * Update dirty state
   */
  const updateDirtyState = () => {
    const changed = !isEqual(initialData.value, currentData.value);
    isDirty.value = changed;
    hasChanges.value = changed;
  };

  /**
   * Mark form as pristine (reset dirty state)
   */
  const markAsPristine = () => {
    // Update initial data to match current data
    if (initialData.value && currentData.value) {
      Object.keys(currentData.value).forEach(key => {
        if (initialData.value[key] !== undefined) {
          initialData.value[key] = deepCompare 
            ? JSON.parse(JSON.stringify(currentData.value[key]))
            : currentData.value[key];
        }
      });
    }
    isDirty.value = false;
    hasChanges.value = false;
  };

  /**
   * Mark form as dirty
   */
  const markAsDirty = () => {
    isDirty.value = true;
    hasChanges.value = true;
  };

  /**
   * Reset form to initial state
   */
  const reset = () => {
    if (initialData.value && currentData.value) {
      Object.keys(initialData.value).forEach(key => {
        if (currentData.value[key] !== undefined) {
          currentData.value[key] = deepCompare
            ? JSON.parse(JSON.stringify(initialData.value[key]))
            : initialData.value[key];
        }
      });
    }
    isDirty.value = false;
    hasChanges.value = false;
  };

  /**
   * Vue Router navigation guard
   */
  const beforeRouteLeave = (to, from, next) => {
    if (isDirty.value) {
      if (window.confirm(warningMessage)) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  };

  /**
   * Browser beforeunload event handler
   */
  const handleBeforeUnload = (event) => {
    if (isDirty.value) {
      event.preventDefault();
      event.returnValue = warningMessage;
      return warningMessage;
    }
  };

  // Watch for changes in current data
  if (currentData && currentData.value) {
    watch(
      () => currentData.value,
      () => {
        updateDirtyState();
        
        // Auto-save functionality
        if (autoSave && isDirty.value) {
          if (autoSaveTimer.value) {
            clearTimeout(autoSaveTimer.value);
          }
          autoSaveTimer.value = setTimeout(() => {
            // Emit auto-save event or call callback
            if (options.onAutoSave) {
              options.onAutoSave();
            }
          }, autoSaveDelay);
        }
      },
      { deep: true }
    );
  }

  /**
   * Set up browser warning on page unload
   */
  const enableBrowserWarning = () => {
    window.addEventListener('beforeunload', handleBeforeUnload);
  };

  /**
   * Remove browser warning
   */
  const disableBrowserWarning = () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };

  return {
    isDirty,
    hasChanges,
    markAsPristine,
    markAsDirty,
    reset,
    beforeRouteLeave,
    enableBrowserWarning,
    disableBrowserWarning,
  };
}
